// THE SINGLE OWNER OF STREAM PLAYBACK. One hls.js lifecycle, one native-HLS
// path, one staleness detector — consumed by every component that shows live
// video.
//
// WHY THIS EXISTS. On 2026-08-01, /live showed VIDEO on six tiles while
// MediaMTX served ZERO paths and nothing had published for 19 hours. The P0
// staleness work was real, tested, and deployed — and it lived in
// CameraFeedPlayer, while the route the operator actually uses (`/live` ->
// pages/LiveView.tsx) carried its OWN inline hls.js player with no staleness
// detection at all. `lib/streamStaleness.ts` had exactly ONE importer, which
// was the tell: a safety module with one importer and a parallel implementation
// beside it is a safety module that covers half the product.
//
// The mirror image existed too: `lib/tileState.ts` was imported only by
// LiveView, and CameraFeedPlayer had no coded tile states. Each player owned
// the safety module the other lacked. That is the defect class ADR-005 names —
// one fact in two places, copies drifted — reaching the one surface where being
// wrong is most dangerous: a bank corridor rendering a nineteen-hour-old frame
// under a "playing" state.
//
// THE PROPERTY THIS HOOK OWNS, and the reason it is a hook rather than a
// component: ANY exit from a live stream — went-stale, never-started, fatal
// error, ended, or the silent no-event publisher death — CLEARS THE PICTURE.
// Not hides it behind a class; clears the element. `className={... : "hidden"}`
// was doing real work on /live, but a hidden element still holds a decoded
// frame, so anything that re-shows it (a layout change, a state flip, a
// fullscreen toggle) repaints nineteen-hour-old video. Hiding is presentation.
// Clearing is the safety property, and it belongs here.
//
// Tile chrome — warm-up, digital zoom, PTZ, snapshot, the mute bus, the retry
// counter, fullscreen, grid layouts, selection — deliberately stays in the
// components. This hook owns the pipeline and the safety property, nothing
// about how a tile looks.
import { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";

import {
  fetchFreshStreamUrl,
  isStreamAuthError,
  msUntilRemint,
} from "@/lib/streamToken";
import {
  evaluateLiveness,
  mustClearPicture,
  type StreamLiveness,
} from "@/lib/streamStaleness";

export type PlaybackState =
  | "idle"
  | "loading"
  | "playing"
  | "error"
  | "offline"
  | "stopped";

/** How often liveness is re-evaluated. 1s gives <=1s overshoot on a 10s
 *  threshold, negligible against the risk being mitigated. */
export const LIVENESS_TICK_MS = 1_000;

/**
 * Backoff ladder for automatic recovery after the picture is cleared.
 *
 * WHY THIS EXISTS. Clearing on a stale verdict is the P0 safety property and
 * is correct. What was NOT correct is that nothing re-armed afterwards: the
 * retry ladder in LiveView is driven solely by ``onFatalError``, which the
 * stale path never calls, so a TEN SECOND gap killed a tile permanently until
 * someone pressed play. On a video wall left up all day that is the difference
 * between a product and a demo.
 *
 * The shape, chosen against the real cadences rather than picked round:
 *   * 2s first — one segment duration. A transient stall (a gap-jump, a single
 *     late segment) is recovered almost immediately, which is the common case;
 *   * then 5s / 15s / 30s — backing off fast enough that a genuinely dead
 *     publisher is not hammered by 16 tiles at once;
 *   * 60s last, then STOP. Bounded on purpose: a camera that is actually down
 *     must come to rest in a visible failed state rather than retry forever and
 *     hide the outage behind a spinner. ~112s of trying in total.
 * The ladder RESETS the moment a fragment arrives, so a wall that recovers gets
 * its full budget back for the next incident.
 */
export const RECOVERY_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000, 60_000];

/**
 * Blank the media element so NO frame survives on screen.
 *
 * Detaching hls.js is NOT enough — the element keeps its last decoded frame
 * until the src is dropped and it is reloaded. Exported because teardown paths
 * in the consuming components must be able to reach exactly this behaviour
 * rather than reimplementing three-quarters of it.
 */
export function clearPicture(video: HTMLVideoElement | null): void {
  if (!video) return;
  try {
    video.pause();
    video.removeAttribute("src");
    video.srcObject = null;
    video.load(); // forces the element back to an empty, poster-less state
  } catch {
    /* best-effort: never let teardown throw into React */
  }
}


/** Compact snapshot of the media element, for the diagnostic log lines.
 *
 * Deliberately string-formatted rather than an object: these land in a browser
 * console read from a support session, where an expanded object is a click
 * nobody makes. Never throws — a diagnostic must not break playback. */
export function mediaDiagnostics(video: HTMLVideoElement | null): string {
  if (!video) return "media=<no element>";
  try {
    const b = video.buffered;
    const end = b && b.length ? b.end(b.length - 1) : null;
    const start = b && b.length ? b.start(0) : null;
    const ct = video.currentTime;
    const ahead = end !== null ? (end - ct).toFixed(1) : "n/a";
    return (
      `media=[currentTime=${ct.toFixed(2)} buffered=${
        start === null ? "empty" : `${start.toFixed(2)}..${end!.toFixed(2)}`
      } aheadOfPlayhead=${ahead}s ranges=${b ? b.length : 0} paused=${
        video.paused
      } readyState=${video.readyState} networkState=${video.networkState}]`
    );
  } catch {
    return "media=<unreadable>";
  }
}

/** WARNING-level record of a non-fatal hls.js error. */
export function logNonFatalHlsError(
  cameraId: string | undefined,
  data: { type?: unknown; details?: unknown; fatal?: unknown },
  video: HTMLVideoElement | null,
): void {
  try {
    console.warn(
      `[sview360] tile.hls_nonfatal: camera=${cameraId ?? "<unknown>"} ` +
        `type=${String(data?.type)} details=${String(data?.details)} ` +
        `fatal=${String(data?.fatal)} ${mediaDiagnostics(video)}`,
    );
  } catch {
    /* a diagnostic must never break playback */
  }
}

export interface UseStreamPlaybackOptions {
  /** The element to play into. Owned by the consumer; cleared by the hook. */
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  /** Signed HLS URL. Null/undefined means "nothing to play". */
  hlsUrl?: string | null;
  /** Camera id — enables stream-token re-mint. Omit and playback dies at token
   *  expiry, which is the pre-existing behaviour for legacy call sites. */
  cameraId?: string;
  /** False => do not even attempt the playlist; the picture is cleared. */
  enabled?: boolean;
  /** Autoplay muted (browsers require muted autoplay). */
  autoplay?: boolean;
  /** Start automatically when a URL is available. `/live` passes false because
   *  it must run its agent-relay WARM-UP first — that warm-up is load-bearing:
   *  every camera on this fleet is agent-relay H.265 whose `_h264` transcode
   *  sibling spins up on demand, so loading the URL before the relay is ready
   *  fetches a manifest that does not exist yet. */
  autoStart?: boolean;
  /** Called on each fatal error the hook could not recover, so a consumer can
   *  drive its own retry ladder / counter without owning the pipeline. */
  onFatalError?: () => void;
  /** Called when playback genuinely begins. */
  onPlaying?: () => void;
}

export interface RecoveryState {
  /** 1-based attempt currently scheduled or running. 0 => not recovering. */
  attempt: number;
  /** Total attempts the ladder allows. */
  max: number;
  /** Ms until the scheduled rebuild, for operator-facing copy. */
  nextInMs: number;
  /** True once the ladder is spent — a distinct, visible terminal state. */
  exhausted: boolean;
}

export interface StreamPlayback {
  state: PlaybackState;
  liveness: StreamLiveness;
  /** Non-null while auto-recovery is in progress or spent. Lets a tile render
   *  "Reconnecting 2/5" instead of a silent black square that never returns. */
  recovery: RecoveryState | null;
  /** True when the picture has been cleared and must not be shown. */
  mustClear: boolean;
  /** The URL actually loaded — may be a re-minted one, not the prop. */
  activeUrl?: string;
  /** Begin (or restart) playback. Idempotent. */
  start: () => void;
  /** Stop, destroy the pipeline, and CLEAR the picture. */
  stop: () => void;
  /** Force a token re-mint (rate-limited, serialized). */
  remint: () => Promise<void>;
}

export function useStreamPlayback({
  videoRef,
  hlsUrl,
  cameraId,
  enabled = true,
  autoplay = true,
  autoStart = true,
  onFatalError,
  onPlaying,
}: UseStreamPlaybackOptions): StreamPlayback {
  const hlsRef = useRef<Hls | null>(null);
  const [state, setState] = useState<PlaybackState>("idle");
  const [liveness, setLiveness] = useState<StreamLiveness>({
    kind: "starting",
    waitingForMs: 0,
  });

  // The liveness clock is reset ONLY by the arrival of a NEW fragment, never by
  // playback position: hls.js keeps playing buffered content for tens of
  // seconds after the publisher dies, so currentTime is not evidence of life.
  const startedAtRef = useRef<number>(Date.now());
  const lastFragmentAtRef = useRef<number | null>(null);
  const clearedRef = useRef(false);

  // ── automatic recovery after a clear ─────────────────────────────────────
  const [recovery, setRecovery] = useState<RecoveryState | null>(null);
  const recoveryAttemptRef = useRef(0);
  const recoveryTimerRef = useRef<number | null>(null);

  const [activeUrl, setActiveUrl] = useState<string | undefined>(
    hlsUrl ?? undefined,
  );
  useEffect(() => {
    setActiveUrl(hlsUrl ?? undefined);
  }, [hlsUrl]);

  // A generation counter so an in-flight async start cannot resurrect a
  // pipeline the consumer has since stopped.
  const genRef = useRef(0);
  const [runToken, setRunToken] = useState(autoStart ? 1 : 0);

  // ── re-mint: serialized + rate-limited so a failing mint cannot spin ──────
  const remintingRef = useRef(false);
  const lastRemintAtRef = useRef(0);
  const remint = useCallback(async () => {
    if (!cameraId || remintingRef.current) return;
    const now = Date.now();
    if (now - lastRemintAtRef.current < 10_000) return;
    remintingRef.current = true;
    lastRemintAtRef.current = now;
    try {
      const fresh = await fetchFreshStreamUrl(cameraId);
      if (fresh) setActiveUrl(fresh);
    } catch {
      /* leave the consumer's error handling in place */
    } finally {
      remintingRef.current = false;
    }
  }, [cameraId]);

  // Proactive re-mint ~60s before expiry so a long-open tile never 401s.
  useEffect(() => {
    if (!cameraId || !enabled || !activeUrl || runToken === 0) return;
    const delay = msUntilRemint(activeUrl, Date.now());
    if (delay === null) return;
    const t = window.setTimeout(() => void remint(), delay);
    return () => window.clearTimeout(t);
  }, [activeUrl, cameraId, enabled, remint, runToken]);

  // ── consumer callbacks live in REFS, never in a dependency array ─────────
  //
  // THE CANCEL STORM (2026-08-04). ``onFatalError``/``onPlaying`` were in the
  // pipeline effect's deps, and LiveView passed them as INLINE arrows — a new
  // identity on every render. The effect's own body calls setLiveness() and
  // setState(), so it re-rendered itself, minted new callback identities, saw
  // "deps changed", ran its cleanup (hls.destroy()) and rebuilt. destroy()
  // aborts every in-flight XHR, so playlists and segments were cancelled
  // 15-53ms after being issued, forever. No fragment ever arrived, so liveness
  // never left "starting", so the tile stayed black with no error.
  //
  // Refs break the cycle at the SOURCE rather than at the call site: a future
  // caller passing an inline arrow — the natural thing to write — cannot
  // reintroduce it. The call site is memoized too, but that is defence in
  // depth, not the fix.
  const onFatalErrorRef = useRef(onFatalError);
  const onPlayingRef = useRef(onPlaying);
  useEffect(() => {
    onFatalErrorRef.current = onFatalError;
    onPlayingRef.current = onPlaying;
  });

  // Cancel any pending recovery on unmount — a timer that fires after the tile
  // is gone bumps state on a dead component.
  useEffect(() => {
    return () => {
      if (recoveryTimerRef.current !== null) {
        window.clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }
    };
  }, []);

  const teardownPipeline = useCallback(() => {
    const hls = hlsRef.current;
    if (hls) {
      hls.destroy();
      hlsRef.current = null;
    }
  }, []);

  /** The safety property, in one place: kill the pipeline AND blank the pixels. */
  const clearAll = useCallback(() => {
    teardownPipeline();
    clearPicture(videoRef.current);
    clearedRef.current = true;
  }, [teardownPipeline, videoRef]);

  const cancelRecovery = useCallback(() => {
    if (recoveryTimerRef.current !== null) {
      window.clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  /** Called when a fragment arrives: the tile is healthy, so the ladder resets.
   *  Guarded on the ref so a healthy stream does not re-render every 2s. */
  const noteRecovered = useCallback(() => {
    if (recoveryAttemptRef.current !== 0) {
      recoveryAttemptRef.current = 0;
      cancelRecovery();
      setRecovery(null);
    }
  }, [cancelRecovery]);

  /**
   * Schedule ONE rebuild after the current backoff step.
   *
   * THE UNBOUNDED-LOOP HAZARD, and why this is safe: the rebuild is triggered
   * by bumping ``runToken``, a pipeline dependency, so each call produces
   * exactly ONE teardown+rebuild. The caller invokes this only on the CLEAR
   * TRANSITION (guarded by ``clearedRef``), never per tick — otherwise the
   * ticker would schedule a retry every second and the cancel storm would be
   * back in a new costume.
   */
  const scheduleRecovery = useCallback(() => {
    const attempt = recoveryAttemptRef.current;
    if (attempt >= RECOVERY_BACKOFF_MS.length) {
      setRecovery({
        attempt,
        max: RECOVERY_BACKOFF_MS.length,
        nextInMs: 0,
        exhausted: true,
      });
      return;
    }
    const delay = RECOVERY_BACKOFF_MS[attempt];
    recoveryAttemptRef.current = attempt + 1;
    setRecovery({
      attempt: attempt + 1,
      max: RECOVERY_BACKOFF_MS.length,
      nextInMs: delay,
      exhausted: false,
    });
    cancelRecovery();
    recoveryTimerRef.current = window.setTimeout(() => {
      recoveryTimerRef.current = null;
      // RE-MINT BEFORE REBUILDING IF THE TOKEN IS DEAD.
      //
      // The ladder used to bump runToken and nothing else, so a rebuild
      // replayed the SAME activeUrl — and if that URL's stream token had
      // expired, all five attempts replayed a dead credential and the tile
      // ended at "Gave up after 5". Five 401s from one client at 12:53 on
      // 2026-08-04, ~4 minutes past expiry, are that signature. One re-mint
      // would have fixed it on attempt 1.
      //
      // The predicate already existed: msUntilRemint returns exp - 60s - now,
      // so <= 0 means "expired or inside the renewal window". remint() is
      // already serialized and rate-limited, so calling it here cannot storm.
      //
      // WAKE-FROM-SLEEP reaches this same path. A laptop lid closed overnight
      // suspends timers; on wake the overdue proactive timer fires once with
      // the token already dead, and the liveness clock — which is wall-clock —
      // reads ancient, so the ticker clears and schedules a ladder attempt.
      // Whichever fires first now re-mints; neither replays a dead token.
      //
      // If the mint FAILS (the app session died overnight too), remint leaves
      // activeUrl untouched and we still bump: the rebuild 401s, the ladder
      // advances, and the tile comes to rest honestly instead of hanging.
      const stale =
        !!activeUrl && (msUntilRemint(activeUrl, Date.now()) ?? 1) <= 0;
      if (stale && cameraId) {
        void remint().finally(() => setRunToken((t) => t + 1));
        return;
      }
      // One bump == one rebuild. The pipeline effect resets the liveness clock,
      // so the tile shows "starting" again rather than a stale frame.
      setRunToken((t) => t + 1);
    }, delay);
  }, [cancelRecovery, activeUrl, cameraId, remint]);

  const start = useCallback(() => {
    recoveryAttemptRef.current = 0;
    cancelRecovery();
    setRecovery(null);
    setRunToken((t) => t + 1);
  }, [cancelRecovery]);

  const stop = useCallback(() => {
    genRef.current += 1;
    setRunToken(0);
    cancelRecovery();
    recoveryAttemptRef.current = 0;
    setRecovery(null);
    clearAll();
    setState("stopped");
  }, [clearAll, cancelRecovery]);

  // ── the liveness ticker — the P0 safety property ─────────────────────────
  useEffect(() => {
    if (!enabled || !activeUrl || runToken === 0) return;
    const id = window.setInterval(() => {
      const next = evaluateLiveness({
        startedAt: startedAtRef.current,
        lastFragmentAt: lastFragmentAtRef.current,
        now: Date.now(),
      });
      // KEEP THE PREVIOUS OBJECT WHEN THE VERDICT IS UNCHANGED.
      //
      // ``evaluateLiveness`` returns a fresh object literal every call, and
      // ``waitingForMs`` / ``staleForMs`` change every tick — so a plain
      // ``setLiveness(next)`` re-rendered the tile once a second forever, which
      // was the engine driving the teardown loop above. Returning ``prev``
      // makes React bail out, so a tile whose verdict has not changed does not
      // re-render at all.
      //
      // Comparing on ``kind`` alone is deliberate: the numeric fields are not
      // rendered anywhere (``livenessMessage`` and ``mustClearPicture`` read
      // only ``kind``), so a changing millisecond count is invisible to the
      // operator and cannot justify a render. If a consumer ever needs a live
      // count, it must take it from its own clock, not from this state.
      //
      // NOT done by widening LIVENESS_TICK_MS: the tick is the P0 staleness
      // guarantee and slowing it would trade operator safety for a symptom.
      setLiveness((prev) => (prev.kind === next.kind ? prev : next));
      if (mustClearPicture(next)) {
        if (!clearedRef.current) {
          // WARNING marker — the browser-console analogue of a server-side
          // logger.warning, so a stale tile is diagnosable from a support
          // session without reproducing it. Once per transition, not per tick.
          // THE DISCRIMINATOR IS CAPTURED HERE, not by someone watching at
          // the right second. currentTime frozen with buffered.end ~30s ahead
          // means playback stopped draining and hls.js stopped fetching
          // because it had nothing to fill; currentTime advancing with an
          // empty buffer means the stream itself stalled. The two need
          // opposite fixes, and this line is what tells them apart after the
          // fact.
          console.warn(
            "[sview360] tile.stream_unavailable: clearing picture for camera " +
              `${cameraId ?? "<unknown>"} — liveness=${next.kind}. A frame is ` +
              "never held once the fragment clock stops advancing. " +
              mediaDiagnostics(videoRef.current),
          );
        }
        // THE silent death: no hls error ever fires, so this tick is the only
        // thing standing between an operator and a frozen frame.
        //
        // Re-arm on the TRANSITION only. clearAll() sets clearedRef, so the
        // schedule happens once per clear and not once per tick — that
        // distinction is the whole difference between a backoff ladder and a
        // retry storm.
        if (!clearedRef.current) scheduleRecovery();
        clearAll();
      }
    }, LIVENESS_TICK_MS);
    return () => window.clearInterval(id);
  }, [enabled, activeUrl, cameraId, clearAll, runToken, scheduleRecovery]);

  // ── the pipeline ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !activeUrl || runToken === 0) {
      // Was, on both players: set a state and return — which left the previous
      // frame painted. A tile with nothing to show must go BLACK, not freeze.
      clearAll();
      setState(enabled ? "stopped" : "offline");
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    const gen = (genRef.current += 1);
    const alive = () => genRef.current === gen;

    // A REBUILD IS HAPPENING NOW, so any QUEUED rebuild is redundant — drop it.
    //
    // Found by an existing test rather than by review: without this, a tile
    // that cleared (retry queued) and then rebuilt for an unrelated reason —
    // a token re-mint swapping activeUrl is the real-world case — still had
    // the old timer fire seconds later. That tore down a pipeline which by
    // then may have been perfectly healthy, resetting the fragment clock and
    // costing a visible reload for nothing. The ATTEMPT COUNTER is deliberately
    // left alone: only a fragment actually arriving proves recovery, and only
    // that resets the ladder.
    cancelRecovery();

    // New source => restart the liveness clock from scratch.
    startedAtRef.current = Date.now();
    lastFragmentAtRef.current = null;
    clearedRef.current = false;
    // Same bail-out as the ticker: on a genuine rebuild the clock refs above
    // are what reset; re-minting an identical "starting" object would only
    // schedule a render that changes nothing on screen.
    setLiveness((prev) =>
      prev.kind === "starting" ? prev : { kind: "starting", waitingForMs: 0 },
    );
    setState("loading");

    const url = activeUrl;

    // Safari handles HLS natively — skip hls.js to avoid double-buffering.
    const canNative = video.canPlayType("application/vnd.apple.mpegurl") !== "";
    if (canNative) {
      video.src = url;
      // Native HLS exposes no fragment events, so approximate the fragment
      // clock with buffered-range growth: `progress` fires as new media is
      // appended. currentTime alone keeps ticking off the existing buffer after
      // upstream death, which is exactly what must not be trusted.
      const onProgress = () => {
        lastFragmentAtRef.current = Date.now();
        noteRecovered();
      };
      const onLoaded = () => {
        lastFragmentAtRef.current = Date.now();
        if (!alive()) return;
        setState("playing");
        onPlayingRef.current?.();
      };
      const onEnded = () => {
        // A live stream that "ends" has lost its publisher. Clearing here is
        // the difference between a black tile and a frozen last frame.
        if (!alive()) return;
        clearAll();
        setState("error");
      };
      const onError = () => {
        if (!alive()) return;
        // A mid-session media error on a long-open tile is almost always token
        // expiry — try one rate-limited re-mint. EITHER WAY the picture is
        // cleared first: the previous implementation on /live called
        // remintAndReplay() then setState("error") and left both the src and
        // the frame in place.
        clearAll();
        setState("error");
        if (cameraId) void remint();
        onFatalErrorRef.current?.();
      };
      video.addEventListener("loadedmetadata", onLoaded);
      video.addEventListener("progress", onProgress);
      video.addEventListener("ended", onEnded);
      video.addEventListener("error", onError);
      if (autoplay) {
        video.muted = true;
        void video.play().catch(() => {
          /* autoplay can be blocked — the user can press play */
        });
      }
      return () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        video.removeEventListener("progress", onProgress);
        video.removeEventListener("ended", onEnded);
        video.removeEventListener("error", onError);
        clearPicture(video);
      };
    }

    if (!Hls.isSupported()) {
      clearAll();
      setState("error");
      return;
    }

    // Sprint-8 reliability tuning, preserved verbatim: lowLatencyMode caused
    // "loaded one segment then froze" against mediamtx's 2s mpegts segments.
    const buildHls = () =>
      new Hls({
        lowLatencyMode: false,
        enableWorker: true,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        backBufferLength: 90,
      });

    const init = () => {
      if (!alive()) return;
      const hls = buildHls();
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.startLoad();

      // THE liveness signal: a fragment was appended, so the publisher is alive
      // right now. The only thing that resets the stale clock.
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        lastFragmentAtRef.current = Date.now();
        // A fragment landed: the tile is healthy again, so the ladder resets
        // and a wall that recovers gets its full budget back next incident.
        noteRecovered();
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!alive()) return;
        setState("playing");
        onPlayingRef.current?.();
        if (autoplay) {
          video.muted = true;
          void video.play().catch(() => {
            /* user gesture required */
          });
        }
      });

      hls.on(Hls.Events.ERROR, (_evt, data) => {
        // NON-FATAL ERRORS ARE NOW LOGGED, AND STILL DO NOT TEAR ANYTHING DOWN.
        //
        // This line used to be a bare ``if (!data.fatal ...) return;`` — so
        // BUFFER_STALLED_ERROR, bufferSeekOverHole and gap-jumps, precisely the
        // class that would explain "the browser stopped fetching", were dropped
        // with no trace. The tile then went stale 10s later and the only
        // evidence of WHY was gone. Same shape as the readiness collapse: the
        // failure had no signal, so it looked like nothing happened.
        //
        // Behaviour is deliberately unchanged — a non-fatal error must not
        // destroy a player that hls.js can still recover on its own.
        if (!data.fatal) {
          if (alive()) logNonFatalHlsError(cameraId, data, videoRef.current);
          return;
        }
        if (!alive()) return;
        // Token expiry: re-minting and rebuilding with the SAME stale URL would
        // 401 again, so mint fresh first (which swaps activeUrl and re-inits).
        if (
          cameraId &&
          data.type === Hls.ErrorTypes.NETWORK_ERROR &&
          isStreamAuthError(data.response?.code)
        ) {
          teardownPipeline();
          void remint();
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          teardownPipeline();
          setTimeout(() => {
            if (videoRef.current && alive()) init();
          }, 1000);
          return;
        }
        // TERMINAL. This is one of the two leaks: both players previously did a
        // bare setState("error") here, leaving the hls instance attached and the
        // src set — so the pipeline kept running and the last frame stayed
        // decoded behind whatever the tile chose to render.
        clearAll();
        setState("error");
        onFatalErrorRef.current?.();
      });
    };

    init();

    return () => {
      teardownPipeline();
    };
  }, [
    activeUrl,
    enabled,
    autoplay,
    cameraId,
    remint,
    runToken,
    clearAll,
    teardownPipeline,
    cancelRecovery,
    videoRef,
    // onFatalError / onPlaying are DELIBERATELY ABSENT — they are read through
    // refs above. They are notifications, not inputs: neither changes what the
    // pipeline should be, so neither can justify tearing one down. Everything
    // still listed here genuinely requires a rebuild (a different URL, a
    // stopped tile, a new run, a different element).
  ]);

  return {
    state,
    liveness,
    recovery,
    mustClear: mustClearPicture(liveness),
    activeUrl,
    start,
    stop,
    remint,
  };
}
